import { getTenants } from '../../../api/organizations';
import { deleteSession } from '../../../api/sessions';
import { showAppToast } from '../../../components/AppToast/showAppToast';
import i18n from '../../../i18n';
import { store } from '../../../store';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import { getItem } from '../../../utils/storage';
import {
  clearGlobalTokens,
  loadGlobalTokens,
  saveActiveTenantIdToStorage,
} from '../../../utils/tenantSessionStorage';
import { selectUser } from '../../Login/LoginSlice';
import { ensureUser } from '../../../routes/loaderHelpers';
import {
  applyActiveTenantFromList,
  selectAllTenantSessions,
  selectTenantId,
  setActiveTenantId,
} from '.';
import { tryDeletePushToken } from '../../Sidebar/actionCreators';
import { activateSessionByTenantId, loadPerTenantTokens } from './tenantTokens';
import { showConfirmDialog } from '../../../utils/ConfirmationServiceContext/confirmationContext';
import { upsertTenantSession } from './upsertTenantSession';
import { clearPreAddOrgTenantId, getPreAddOrgTenantId } from './beginAddOrganization';
import { switchActiveOrganization } from './switchActiveOrganization';
import { syncInactiveSessionAlarms } from './syncSessionIndicators';

export type EnsureTenantResult = { ok: true; tenantId: number } | { ok: false; response: Response };

/** Ensures the active tenant row is loaded into Redux. Used by route loaders. */
export async function ensureTenant({
  shouldRefresh = false,
}: {
  shouldRefresh?: boolean;
} = {}): Promise<EnsureTenantResult> {
  if (!shouldRefresh) {
    const tenantId = selectTenantId(store.getState());
    if (tenantId) return { ok: true, tenantId };
  }

  const tenantsResponse = await getTenants();
  if (tenantsResponse.error) {
    return {
      ok: false,
      response: new Response('Failed to fetch tenant', {
        status: tenantsResponse.error.error?.httpStatus ?? 500,
      }),
    };
  }

  const tenants = tenantsResponse.data.data;
  if (!tenants?.length) {
    return { ok: false, response: new Response('Failed to fetch tenant', { status: 401 }) };
  }

  const tenant = store.dispatch(applyActiveTenantFromList(tenants[0]));
  return { ok: true, tenantId: tenant.id };
}

/** Registers or refreshes the active tenant session after login/exchange. */
export async function registerActiveTenantSession(tenantId: number) {
  if (!isMultiOrgFeatureEnabled()) {
    // Delete existing push token on first login to ensure it is not erronously still connected to an earlier session
    await tryDeletePushToken();
    return;
  }

  const globalTokens = loadGlobalTokens();
  if (!globalTokens) return;

  const state = store.getState();

  const region = getItem('selectedRegion');
  const tenant = state.organizations.tenant;
  if (!tenant) return;
  const user = selectUser(state);
  if (!user) return;

  // Only treat this as an add-org login when there are already cached sessions
  // (a stale snapshot key from an aborted flow plus a fresh login would otherwise
  // incorrectly trigger add-org logic).
  const previousTenantId = getPreAddOrgTenantId();

  let errorMessage;
  const existingTenantSessions = selectAllTenantSessions(state);
  const isAdditionalTenantSession = previousTenantId != null && existingTenantSessions.length > 0;
  if (isAdditionalTenantSession) {
    const hasAlreadyTenantSession = existingTenantSessions.some(t => t.tenantId === tenantId);
    const hasAnotherRegion = existingTenantSessions.filter(s => s.region !== region).length > 0;
    if (hasAlreadyTenantSession) {
      errorMessage = 'tenant_session_add_error_duplicate_tenant';
    } else if (hasAnotherRegion) {
      errorMessage = 'tenant_session_add_error_cross_region';
    }

    if (errorMessage) {
      await deleteSession(globalTokens.sessionId, globalTokens.accessToken);
      clearGlobalTokens();
      const fallbackTenantId = previousTenantId ?? existingTenantSessions[0]?.tenantId;
      if (fallbackTenantId) {
        store.dispatch(setActiveTenantId(fallbackTenantId));
        saveActiveTenantIdToStorage(fallbackTenantId);
        activateSessionByTenantId(fallbackTenantId);
        await ensureTenant({ shouldRefresh: true });
        await ensureUser({ tenantId: fallbackTenantId, shouldRefresh: true });
        showConfirmDialog({
          title: i18n.t('failed'),
          description: i18n.t(errorMessage),
          onSubmit: () => {},
        });
      }
      clearPreAddOrgTenantId();
      return;
    }
  } else if (!existingTenantSessions.length) {
    // Delete existing push token on first login to ensure it is not erronously still connected to an earlier session
    await tryDeletePushToken();
  }

  store.dispatch(
    upsertTenantSession({
      tenantId,
      tenantName: tenant.name,
      tenantLogo: tenant.logoUrl,
      userId: user.id,
      email: user.email,
      region,
      isCoAlert: false, // CoAlert login is currently only available in the mobile app
      ...globalTokens,
    })
  );

  clearPreAddOrgTenantId();

  if (isAdditionalTenantSession) {
    showAppToast({
      variant: 'success',
      message: i18n.t('add_organization_success', { name: tenant.name }),
    });
    // Refresh indicator flags for the previously-active tenant (now inactive)
    // — mirrors `switchActiveOrganization`. Fire and forget; failure is
    // non-fatal drift correction.
    store.dispatch(syncInactiveSessionAlarms()).catch(error => {
      console.error('[loaderHelpers] syncInactiveSessionAlarms failed after add-org', error);
    });
  }
}

/**
 * Honour the `?tenantId=<id>` deep link when multi-org is on and the tenant is
 * already cached. Triggers a full `switchActiveOrganization` so tokens, region
 * and tenant-scoped Redux are updated consistently. No-ops when the tenant is
 * not cached — the normal login flow will add it.
 */
export async function honourTenantIdQueryParam(request: Request) {
  if (!isMultiOrgFeatureEnabled()) return;

  const url = new URL(request.url);
  const raw = url.searchParams.get('tenantId');
  if (!raw) return;

  const tenantId = Number(raw);
  if (!Number.isFinite(tenantId) || tenantId <= 0) return;

  const cached = loadPerTenantTokens(tenantId);
  if (!cached) return;

  await store.dispatch(switchActiveOrganization(tenantId));
}
